/*
 * FarmSteady — back-in-stock (BIS) email capture controller.
 *
 * Loaded (deferred) by snippets/fs-back-in-stock.liquid — PDP-only, never
 * from theme.liquid. Posts directly to Klaviyo's client Back in Stock API:
 *
 *   POST https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id={KEY}
 *   headers: Content-Type: application/json, revision: KLAVIYO_REVISION
 *   Success is 202 Accepted with an empty body.
 *
 * The catalog-variant id uses Klaviyo's Shopify catalog id scheme:
 * "$shopify:::$default:::<numeric Shopify variant id>".
 *
 * Progressive enhancement: the module ships display:none (farmsteady-bis.css).
 * This script adds .fs-bis--enhanced on init and toggles .fs-bis--active by
 * variant availability — the form only appears when both are present, so
 * no-JS visitors never see a dead form.
 *
 * Variant freshness: subscribes to Dawn's PUB_SUB_EVENTS.variantChange
 * (constants.js + pubsub.js load with defer in theme.liquid before this
 * file, so deferred execution order guarantees they exist; still guarded).
 * Fallback mirrors the product form's input[name="id"], same approach as
 * assets/farmsteady-sticky-atc.js.
 *
 * Sticky ATC handoff: when the sticky chip renders its sold-out state
 * (sections/farmsteady-sticky-atc.liquid), this script hides the inert
 * disabled button ([data-fs-sticky-atc-soldout]) and reveals a "Notify me"
 * jump button ([data-fs-bis-jump]) that scrolls to this form.
 */

(function () {
  'use strict';

  const KLAVIYO_REVISION = '2024-10-15';
  const KLAVIYO_BIS_ENDPOINT = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=';
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  const COPY = {
    success: "You're on the list.",
    invalidEmail: 'Please enter a valid email address.',
    failure: "Something went wrong. Please try again in a minute.",
  };

  const READY_STATES = ['interactive', 'complete'];

  function setStatus(root, kind, message) {
    const status = root.querySelector('[data-fs-bis-status]');
    if (!status) return;
    status.textContent = message;
    status.classList.toggle('fs-bis__status--success', kind === 'success');
    status.classList.toggle('fs-bis__status--error', kind === 'error');
  }

  function setActive(root, unavailable) {
    root.classList.toggle('fs-bis--active', Boolean(unavailable));
    // Sold out = no accelerated-checkout path either: hide Shop Pay +
    // "More payment options" while the current variant is unavailable.
    const buttons = document.querySelectorAll('.product-form__buttons');
    buttons.forEach((el) => el.classList.toggle('fs-oos', Boolean(unavailable)));
  }

  function buildPayload(email, variantId) {
    return {
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          channels: ['EMAIL'],
          profile: {
            data: {
              type: 'profile',
              attributes: { email: email },
            },
          },
        },
        relationships: {
          variant: {
            data: {
              type: 'catalog-variant',
              id: '$shopify:::$default:::' + variantId,
            },
          },
        },
      },
    };
  }

  function wireSubmit(root) {
    const form = root.querySelector('[data-fs-bis-form]');
    if (!form) return;
    const input = form.querySelector('input[type="email"]');
    const submit = form.querySelector('.fs-bis__submit');

    form.addEventListener('submit', (event) => {
      event.preventDefault();

      const email = input ? input.value.trim() : '';
      if (!EMAIL_RE.test(email)) {
        setStatus(root, 'error', COPY.invalidEmail);
        if (input) input.focus();
        return;
      }

      const variantId = root.dataset.variantId;
      const companyId = root.dataset.companyId;
      if (!variantId || !companyId || !window.fetch) {
        setStatus(root, 'error', COPY.failure);
        return;
      }

      if (submit) submit.disabled = true;
      setStatus(root, '', '');

      fetch(KLAVIYO_BIS_ENDPOINT + encodeURIComponent(companyId), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          revision: KLAVIYO_REVISION,
        },
        body: JSON.stringify(buildPayload(email, variantId)),
      })
        .then((response) => {
          if (!response.ok) throw new Error('Klaviyo BIS responded ' + response.status);
          root.classList.add('fs-bis--subscribed');
          setStatus(root, 'success', COPY.success);
        })
        .catch(() => {
          setStatus(root, 'error', COPY.failure);
        })
        .finally(() => {
          if (submit) submit.disabled = false;
        });
    });
  }

  function wireVariantChanges(root) {
    // Primary: Dawn's pub/sub. The payload's `variant` is undefined when the
    // shopper lands on a non-existent option combination — hide the form then.
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
      subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        const variant = event && event.data && event.data.variant;
        if (!variant) {
          setActive(root, false);
          return;
        }
        root.dataset.variantId = String(variant.id);
        root.classList.remove('fs-bis--subscribed');
        setStatus(root, '', '');
        setActive(root, !variant.available);
      });
      return;
    }

    // Fallback: mirror the main product form's hidden variant input. This
    // keeps the id fresh but can't see availability, so the module keeps its
    // server-rendered active state.
    const mainVariantInput = document.querySelector(
      'product-info input[name="id"], product-form input[name="id"], .product-form input[name="id"]'
    );
    if (mainVariantInput) {
      mainVariantInput.addEventListener('change', () => {
        if (mainVariantInput.value) root.dataset.variantId = mainVariantInput.value;
      });
    }
  }

  function wireStickyJump(root) {
    const jump = document.querySelector('[data-fs-bis-jump]');
    if (!jump) return;
    const inertSoldOut = document.querySelector('[data-fs-sticky-atc-soldout]');
    if (inertSoldOut) inertSoldOut.hidden = true;
    jump.hidden = false;
    jump.addEventListener('click', () => {
      root.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const input = root.querySelector('input[type="email"]');
      if (input) input.focus({ preventScroll: true });
    });
  }

  function init() {
    // Usually one module per page (main PDP buy box), but featured-product
    // sections also render buy-buttons — enhance every instance. The sticky
    // ATC jump targets the first (the main buy box renders first).
    const roots = document.querySelectorAll('[data-fs-bis]');
    if (!roots.length) return;

    roots.forEach((root) => {
      root.classList.add('fs-bis--enhanced');
      setActive(root, root.dataset.variantAvailable !== 'true');
      wireSubmit(root);
      wireVariantChanges(root);
    });

    wireStickyJump(roots[0]);
  }

  if (READY_STATES.includes(document.readyState)) {
    init();
  } else {
    document.addEventListener('DOMContentLoaded', init);
  }
})();
